import { StanButton, StanModal } from '@/components';
import { useStoreItem } from '@/features/theme-editor/hooks/useStoreItem';
import { useCustomizationStore } from '@/features/theme-editor/stores/customizationStore';
import { ItemType, PageItem } from '@/features/theme-editor/types/store';
import { useModal } from '@/hooks/useModal';
import { configSettings, PRODUCT_TYPES } from '@/lib';
import { useDefaultStore } from '@/stores';
import { Page } from '@/types/pageSchema';
import { MenuItem } from '../../Menu';
import { Panel, PanelFooter } from '../../Panel';
import { Button } from '../../ui/Button';
import { Icon } from '../../ui/Icon';
import { Label } from '../../ui/Label';

enum ProductPreview {
    Button = 'button',
    Callout = 'callout',
    Preview = 'preview',
}

interface Props {
    id: number;
}

const productPreview = (page: Page) => {
    const noPreview = !(page.type === PRODUCT_TYPES.LEAD_MAGNET);
    const preview = {
        [ProductPreview.Button]: noPreview,
        [ProductPreview.Callout]: noPreview,
        [ProductPreview.Preview]: noPreview && ['meeting', 'webinar', 'course', 'digital-download'].includes(page.type),
    };

    return preview;
};

export const ProductLayout = ({ id }: Props) => {
    const { updateItem, pages, isDirty } = useCustomizationStore();
    const { storeId } = useDefaultStore();
    const item = useStoreItem(id);
    const page = pages.find(p => p.page_id === item?.id);

    const { isOpen: isModalOpen, open: openModal, close: closeModal } = useModal();

    if (!item || !page || item.type !== ItemType.PAGE) return null;
    const previews = productPreview(page);
    const pageData = item.data?.data ?? page.data;
    const button = pageData?.button;

    const handleLayoutChange = (layout: ProductPreview) => {
        const currentPageData = item.data?.data ?? page.data;
        const updatedButton = { ...(currentPageData?.button ?? {}), layout };
        const updatedPage = { ...(item.data ?? page), data: { ...currentPageData, button: updatedButton } };

        updateItem({
            ...item,
            data: updatedPage,
        } as PageItem);
    };

    const hasOptions =
        previews[ProductPreview.Button] || previews[ProductPreview.Callout] || previews[ProductPreview.Preview];

    const legacyPageType = (page.type as string) === 'meeting' ? PRODUCT_TYPES.WEBINAR : page.type;
    const legacyUrl = `${configSettings.adminDomain}stores/${storeId}/page/${legacyPageType}/${page.page_id}?skipModal=true`;
    const productPagePath = legacyUrl;

    return (
        <Panel
            footer={
                <PanelFooter id={id}>
                    <Button
                        onClick={() => {
                            if (isDirty) openModal();
                            else window.location.href = productPagePath;
                        }}
                        className='flex-shrink-0 gap-1'
                        icon={<Icon name='edit' />}
                    >
                        Edit product
                    </Button>
                </PanelFooter>
            }
        >
            {hasOptions && (
                <Label label='Choose your product style'>
                    <div className='flex flex-col gap-4'>
                        {previews[ProductPreview.Button] && (
                            <MenuItem
                                label='Button'
                                description='Ideal for showing many products at once.'
                                icon={<Icon name='button' />}
                                active={button?.layout === ProductPreview.Button}
                                onClick={() => handleLayoutChange(ProductPreview.Button)}
                            />
                        )}
                        {previews[ProductPreview.Callout] && (
                            <MenuItem
                                label='Callout'
                                description='Each product gets full-width focus.'
                                icon={<Icon name='callout' />}
                                active={button?.layout === ProductPreview.Callout}
                                onClick={() => handleLayoutChange(ProductPreview.Callout)}
                            />
                        )}
                        {previews[ProductPreview.Preview] && (
                            <MenuItem
                                label='Preview'
                                description='Great for highlighting products with extra details.'
                                icon={<Icon name='preview' />}
                                active={button?.layout === ProductPreview.Preview}
                                onClick={() => handleLayoutChange(ProductPreview.Preview)}
                            />
                        )}
                    </div>
                </Label>
            )}
            <p className='text-theme-fg-secondary text-sm'>To edit your product info, go back to My Store in admin</p>
            <StanModal id='product-layout-modal' isOpen={isModalOpen} onClose={() => closeModal()} hideCloseButton>
                <div className='flex flex-col items-center justify-center gap-2 text-center'>
                    <h2>Unsaved changes!</h2>
                    <p className='text-[var(--text-color-gray)]'>
                        This will take you to the product page where you can edit the product details. This means that
                        any changes you make here will not be saved.
                    </p>
                    <div className='mt-3 flex gap-2'>
                        <StanButton
                            name='cancel-product-layout-navigation'
                            color='secondary'
                            onClick={() => closeModal()}
                        >
                            Cancel
                        </StanButton>
                        <StanButton
                            name='product-layout-navigation'
                            onClick={() => {
                                closeModal();
                                window.location.href = productPagePath;
                            }}
                        >
                            Take me to the product page
                        </StanButton>
                    </div>
                </div>
            </StanModal>
        </Panel>
    );
};
